import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { logementAPI } from '../../service/api';
import { FiImage, FiUpload, FiX, FiArrowLeft, FiSave, FiLink } from 'react-icons/fi';
import { resolveUploadUrl } from '../../config/config';
import { showSuccess } from '../../service/swal';

const types = ['studio', 'appartement', 'maison', 'chambre'];
const typeLabels = { studio: 'Studio', appartement: 'Appartement', maison: 'Maison', chambre: 'Chambre' };

export default function LogementForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [form, setForm] = useState({
    titre: '', description: '', adresse: '', prix: '', type: 'studio', surface: '', nbPieces: 1, mapsEmbedUrl: ''
  });
  const [images, setImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [urlImages, setUrlImages] = useState([]);
  const [imageUrlInput, setImageUrlInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isEdit) {
      setLoading(true);
      logementAPI.getById(id)
        .then(res => {
          const p = res.data.data;
          setForm({
            titre: p.titre || '',
            description: p.description || '',
            adresse: p.adresse || '',
            prix: p.prix || '',
            type: p.type || 'studio',
            surface: p.surface || '',
            nbPieces: p.nbPieces || 1,
            mapsEmbedUrl: p.mapsEmbedUrl || ''
          });
          setExistingImages(p.images || []);
        })
        .catch(() => setError('Impossible de charger le logement'))
        .finally(() => setLoading(false));
    }
  }, [id, isEdit]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleFiles = (e) => {
    const files = Array.from(e.target.files || []);
    const newPreviews = files.map(f => URL.createObjectURL(f));
    setImages(prev => [...prev, ...files]);
    setPreviews(prev => [...prev, ...newPreviews]);
  };

  const removeNewImage = (index) => {
    URL.revokeObjectURL(previews[index]);
    setImages(prev => prev.filter((_, i) => i !== index));
    setPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddUrl = async () => {
    const url = imageUrlInput.trim();
    if (!url) return;

    if (isEdit) {
      try {
        await logementAPI.addImageUrl(id, url);
        setExistingImages(prev => [...prev, url]);
        setImageUrlInput('');
      } catch (err) {
        setError('Erreur lors de l\'ajout de l\'URL');
      }
    } else {
      setUrlImages(prev => [...prev, url]);
      setImageUrlInput('');
    }
  };

  const removeUrlImage = (index) => {
    setUrlImages(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingImage = async (imageUrl) => {
    try {
      await logementAPI.deleteImage(id, imageUrl);
      setExistingImages(prev => prev.filter(img => img !== imageUrl));
    } catch (err) {
      setError('Erreur lors de la suppression de l\'image');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      let property;

      if (isEdit) {
        const res = await logementAPI.update(id, {
          ...form,
          prix: parseFloat(form.prix),
          surface: form.surface ? parseInt(form.surface) : null,
          nbPieces: parseInt(form.nbPieces)
        });
        property = res.data.data;
      } else {
        const res = await logementAPI.create({
          ...form,
          prix: parseFloat(form.prix),
          surface: form.surface ? parseInt(form.surface) : null,
          nbPieces: parseInt(form.nbPieces),
          images: urlImages.length > 0 ? urlImages : undefined
        });
        property = res.data.data;
      }

      if (images.length > 0) {
        const formData = new FormData();
        images.forEach(img => formData.append('images', img));
        await logementAPI.uploadImages(property.id, formData);
      }

      showSuccess(isEdit ? 'Logement modifié' : 'Logement publié');
      navigate(`/logements/${property.id}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Une erreur est survenue');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-2" style={{ borderColor: '#C59B6C', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  return (
    <div className="page-container py-8">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider mb-6"
        style={{ color: '#7A828A' }}>
        <FiArrowLeft /> Retour
      </button>

      <div className="mb-8">
        <h1 className="font-serif text-3xl font-bold tracking-tight" style={{ color: '#0A192F' }}>
          {isEdit ? 'Modifier le logement' : 'Publier une annonce'}
        </h1>
        <p className="text-sm mt-1" style={{ color: '#7A828A' }}>
          {isEdit ? 'Modifiez les informations de votre logement' : 'Créez une nouvelle annonce de logement'}
        </p>
      </div>

      {error && (
        <div className="card-root p-4 mb-6 border-2 border-red-600 text-red-600 text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="card-root p-6">
            <h2 className="font-serif font-bold text-base mb-4" style={{ color: '#0A192F' }}>Informations</h2>

            <div className="space-y-4">
              <div>
                <label className="label-root mb-1 block">Titre</label>
                <input className="input-root w-full" name="titre" value={form.titre} onChange={handleChange} required />
              </div>
              <div>
                <label className="label-root mb-1 block">Description</label>
                <textarea className="input-root w-full" name="description" value={form.description} onChange={handleChange} rows={4} />
              </div>
              <div>
                <label className="label-root mb-1 block">Adresse</label>
                <input className="input-root w-full" name="adresse" value={form.adresse} onChange={handleChange} required />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-root mb-1 block">Prix ($/mois)</label>
                  <input className="input-root w-full" type="number" name="prix" value={form.prix} onChange={handleChange} required min={0} />
                </div>
                <div>
                  <label className="label-root mb-1 block">Type</label>
                  <select className="input-root w-full" name="type" value={form.type} onChange={handleChange}>
                    {types.map(t => (
                      <option key={t} value={t}>{typeLabels[t]}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-root mb-1 block">Surface (m²)</label>
                  <input className="input-root w-full" type="number" name="surface" value={form.surface} onChange={handleChange} min={0} />
                </div>
                <div>
                  <label className="label-root mb-1 block">Nombre de pièces</label>
                  <input className="input-root w-full" type="number" name="nbPieces" value={form.nbPieces} onChange={handleChange} min={1} />
                </div>
              </div>
              <div>
                <label className="label-root mb-1 block">Google Maps (iframe)</label>
                <textarea className="input-root w-full" name="mapsEmbedUrl" value={form.mapsEmbedUrl} onChange={handleChange}
                  rows={3} placeholder="Collez le code iframe Google Maps pour localiser votre logement..." />
              </div>
            </div>
          </div>

          <div className="card-root p-6">
            <h2 className="font-serif font-bold text-base mb-4" style={{ color: '#0A192F' }}>Images</h2>

            {existingImages.length > 0 && (
              <div className="mb-4">
                <p className="label-root mb-2">Images existantes</p>
                <div className="flex flex-wrap gap-3">
                  {existingImages.map((url, i) => (
                    <div key={i} className="relative group">
                      <img src={resolveUploadUrl(url)} alt={`Image ${i + 1}`} className="w-28 h-28 object-cover rounded" />
                      <button type="button" onClick={() => removeExistingImage(url)}
                        className="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center rounded-full bg-red-600 text-white text-xs opacity-0 group-hover:opacity-100 transition">
                        <FiX />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center gap-2 mb-4">
              <input className="input-root flex-1" placeholder="Coller une URL d'image..." value={imageUrlInput}
                onChange={(e) => setImageUrlInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddUrl())} />
              <button type="button" onClick={handleAddUrl} className="btn-outline-root flex items-center gap-2">
                <FiLink /> Ajouter
              </button>
            </div>

            {!isEdit && urlImages.length > 0 && (
              <div className="mb-4">
                <p className="label-root mb-2">Images par lien</p>
                <div className="flex flex-wrap gap-3">
                  {urlImages.map((url, i) => (
                    <div key={i} className="relative group">
                      <img src={resolveUploadUrl(url)} alt={`Lien ${i + 1}`} className="w-28 h-28 object-cover rounded"
                        onError={(e) => { e.target.src = ''; e.target.className = 'w-28 h-28 flex items-center justify-center bg-parchment-dark rounded'; e.target.alt = 'Image indisponible'; }} />
                      <button type="button" onClick={() => removeUrlImage(i)}
                        className="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center rounded-full bg-red-600 text-white text-xs opacity-0 group-hover:opacity-100 transition">
                        <FiX />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-3">
              {previews.map((preview, i) => (
                <div key={i} className="relative group">
                  <img src={preview} alt={`Aperçu ${i + 1}`} className="w-28 h-28 object-cover rounded" />
                  <button type="button" onClick={() => removeNewImage(i)}
                    className="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center rounded-full bg-red-600 text-white text-xs opacity-0 group-hover:opacity-100 transition">
                    <FiX />
                  </button>
                </div>
              ))}
              <label className="w-28 h-28 flex flex-col items-center justify-center rounded cursor-pointer border-2 border-dashed" style={{ borderColor: '#E5E5E0', color: '#7A828A' }}>
                <FiUpload className="w-6 h-6 mb-1" />
                <span className="text-[11px] font-semibold">Ajouter</span>
                <input type="file" accept="image/*" multiple className="hidden" onChange={handleFiles} />
              </label>
            </div>
          </div>
        </div>

        <div>
          <div className="card-root p-6" style={{ position: 'sticky', top: '6rem' }}>
            <button type="submit" disabled={saving} className="btn-primary-root w-full py-3 flex items-center justify-center gap-2">
              {saving ? (
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
              ) : (
                <><FiSave /> {isEdit ? 'Enregistrer' : 'Publier'}</>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}